'use client';

import { useState } from 'react';

import { appRoutes } from '@/appRoutes';
import { useLogoutMutation } from '@/features/auth/api/authApi';
import { hardRedirect } from '@/features/auth/utils';
import {
  discardPersistedTimeTracker,
  useHasActivePersistedTimeTracker,
} from '@/features/time-entries/time-tracker';
import { useAppSelector } from '@/store/hooks';
import { ConfirmActionModal, InlineButton, MainButton } from '@web/ui';
import { clearWizardDrafts } from '@web/utils/src/wizardStorage';

type LogoutButtonProps = {
  variant?: 'linkStyle' | 'buttonStyle';
};

export function LogoutButton({ variant = 'linkStyle' }: LogoutButtonProps) {
  const [logout, { isLoading }] = useLogoutMutation();
  const currentWorkspace = useAppSelector((s) => s.workspaces.currentWorkspace);
  const hasActiveTimeTracker = useHasActivePersistedTimeTracker(
    currentWorkspace?.id,
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const executeLogout = async () => {
    try {
      await logout().unwrap();
    } catch (error) {
      console.error('Failed to logout', error);
    }

    // * Also clear any onboarding wizard drafts (local/session).
    try {
      clearWizardDrafts();
    } catch (error) {
      console.error('Failed to clear onboarding drafts', error);
    }

    hardRedirect(appRoutes.auth.index);
  };

  const handleLogout = () => {
    if (hasActiveTimeTracker) {
      setIsConfirmOpen(true);
      return;
    }

    void executeLogout();
  };

  const button =
    variant === 'linkStyle' ? (
      <InlineButton
        onClick={handleLogout}
        ariaLabel="Logout"
        isLoading={isLoading}
        disabled={isLoading}
      >
        Logout
      </InlineButton>
    ) : (
      <MainButton
        onClick={handleLogout}
        ariaLabel="Logout"
        isLoading={isLoading}
        disabled={isLoading}
      >
        Logout
      </MainButton>
    );

  return (
    <>
      {button}
      <ConfirmActionModal
        open={isConfirmOpen}
        title="Log out?"
        description="Logging out will discard the active timer for this workspace."
        confirmButtonLabel="Discard timer and log out"
        cancelButtonLabel="Stay here"
        requireConfirmationText={false}
        isProcessing={isLoading}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          setIsConfirmOpen(false);
          discardPersistedTimeTracker(currentWorkspace?.id);
          void executeLogout();
        }}
      />
    </>
  );
}
